import * as Tone from "tone";
import { AUDIO_FEEL_CONFIG } from "../audioFeelConfig";

// Tone.js sources (Noise, Oscillator, MembraneSynth, …) require strictly
// increasing start times. On mobile the audio clock advances in coarse steps and
// lags right after resume, so two back-to-back triggers can resolve to the same
// Tone.now() value → "Start time must be strictly greater than previous start
// time". This epsilon (2 ms, inaudible) guarantees a strictly increasing schedule.
const TRIGGER_TIME_EPSILON = 0.002;

export interface EmotionalAudioUpdate {
  dt: number;
  elapsed: number;
  danger: number;
  rearThreat: number;
  speed: number;
  speedRatio: number;
  isChampion: boolean;
}

export class EmotionalAudioSystem {
  private master?: Tone.Gain;
  private breathNoise?: Tone.Noise;
  private breathFilter?: Tone.Filter;
  private breathGain?: Tone.Gain;
  private dangerOsc?: Tone.Oscillator;
  private dangerFilter?: Tone.Filter;
  private dangerGain?: Tone.Gain;
  private heartbeat?: Tone.MembraneSynth;
  private footstep?: Tone.NoiseSynth;
  private pickupSynth?: Tone.PolySynth<Tone.Synth>;
  private eventSynth?: Tone.PolySynth<Tone.Synth>;
  private started = false;
  private muted = false;
  private heartbeatCooldown = 0;
  private footstepCooldown = 0;
  private stepSide = 0;
  // Monotonic schedule clock shared by every trigger so start times are always
  // strictly increasing across the whole audio graph (see TRIGGER_TIME_EPSILON).
  private lastTriggerTime = 0;

  async start() {
    await Tone.start();
    this.ensureGraph();
    if (!this.started) {
      this.breathNoise?.start();
      this.dangerOsc?.start();
      this.started = true;
    }
    this.heartbeatCooldown = 0;
    this.footstepCooldown = 0;
    this.lastTriggerTime = 0;
    this.setMuted(this.muted);
  }

  pause() {
    this.breathGain?.gain.rampTo(0, 0.12);
    this.dangerGain?.gain.rampTo(0, 0.12);
  }

  resume() {
    this.setMuted(this.muted);
  }

  stop() {
    this.breathGain?.gain.rampTo(0, 0.18);
    this.dangerGain?.gain.rampTo(0, 0.18);
    this.heartbeatCooldown = 0;
    this.footstepCooldown = 0;
  }

  setMuted(muted: boolean) {
    this.muted = muted;
    if (!this.master) return;
    this.master.gain.rampTo(muted ? 0 : Tone.dbToGain(AUDIO_FEEL_CONFIG.masterVolumeDb), 0.08);
  }

  update({ dt, elapsed, danger, rearThreat, speed, speedRatio, isChampion }: EmotionalAudioUpdate) {
    if (!this.started || !this.master) return;
    const threat = Math.max(danger, rearThreat);
    const breathDb = this.lerp(AUDIO_FEEL_CONFIG.wolfBreathMinDb, AUDIO_FEEL_CONFIG.wolfBreathMaxDb, Math.min(1, speedRatio * 0.75 + threat * 0.5));
    const breathCutoff = this.lerp(AUDIO_FEEL_CONFIG.wolfBreathFilterMin, AUDIO_FEEL_CONFIG.wolfBreathFilterMax, Math.min(1, speedRatio + threat * 0.35));
    const breathPulse = 0.84 + Math.sin(elapsed * 5.2) * 0.16;
    this.breathGain?.gain.rampTo(Tone.dbToGain(breathDb) * breathPulse, 0.08);
    this.breathFilter?.frequency.rampTo(breathCutoff, 0.12);

    const droneDb = this.lerp(AUDIO_FEEL_CONFIG.dangerDroneMinDb, AUDIO_FEEL_CONFIG.dangerDroneMaxDb, threat);
    const droneHz = this.lerp(AUDIO_FEEL_CONFIG.dangerDroneBaseHz, AUDIO_FEEL_CONFIG.dangerDroneMaxHz, threat);
    this.dangerGain?.gain.rampTo(Tone.dbToGain(droneDb), 0.16);
    this.dangerOsc?.frequency.rampTo(droneHz + Math.sin(elapsed * 1.7) * 2.5, 0.18);
    this.dangerFilter?.frequency.rampTo(180 + threat * 620, 0.16);

    this.updateHeartbeat(dt, threat);
    this.updateFootsteps(dt, speed, speedRatio, isChampion);
  }

  playPickup(energy: number) {
    this.ensureGraph();
    const notes = AUDIO_FEEL_CONFIG.pickupNotes;
    const base = this.reserveTime(0.055);
    this.safeTrigger("pickup", () => {
      this.pickupSynth?.triggerAttackRelease(notes[energy % notes.length], "16n", base, 0.82);
      this.pickupSynth?.triggerAttackRelease(notes[(energy + 1) % notes.length], "16n", base + 0.055, 0.52);
    });
  }

  playChampionCharge() {
    this.ensureGraph();
    const base = this.reserveTime(0.16);
    this.safeTrigger("championCharge", () => {
      this.eventSynth?.triggerAttackRelease(["E4", "B4", "E5"], "8n", base, 0.55);
      this.eventSynth?.triggerAttackRelease(["G5", "B5"], "16n", base + 0.16, 0.42);
    });
  }

  playLose() {
    this.ensureGraph();
    const time = this.reserveTime();
    this.safeTrigger("lose", () => this.eventSynth?.triggerAttackRelease(["C3", "G2"], "2n", time, 0.72));
  }

  playWin() {
    this.ensureGraph();
    const base = this.reserveTime(0.22);
    this.safeTrigger("win", () => {
      this.eventSynth?.triggerAttackRelease(["E4", "B4", "D#5", "F#5"], "4n", base, 0.66);
      this.eventSynth?.triggerAttackRelease(["B5", "E6"], "8n", base + 0.22, 0.48);
    });
  }

  private ensureGraph() {
    if (this.master) return;
    this.master = new Tone.Gain(Tone.dbToGain(AUDIO_FEEL_CONFIG.masterVolumeDb)).toDestination();

    this.breathFilter = new Tone.Filter(240, "lowpass");
    this.breathGain = new Tone.Gain(0);
    this.breathNoise = new Tone.Noise("brown").connect(this.breathFilter);
    this.breathFilter.connect(this.breathGain);
    this.breathGain.connect(this.master);

    this.dangerFilter = new Tone.Filter(180, "lowpass");
    this.dangerGain = new Tone.Gain(0);
    this.dangerOsc = new Tone.Oscillator(AUDIO_FEEL_CONFIG.dangerDroneBaseHz, "sine").connect(this.dangerFilter);
    this.dangerFilter.connect(this.dangerGain);
    this.dangerGain.connect(this.master);

    this.heartbeat = new Tone.MembraneSynth({
      pitchDecay: 0.02,
      octaves: 2,
      oscillator: { type: "sine" },
      envelope: { attack: 0.001, decay: 0.18, sustain: 0.01, release: 0.12 }
    }).connect(this.master);
    this.footstep = new Tone.NoiseSynth({
      noise: { type: "brown" },
      envelope: { attack: 0.001, decay: 0.035, sustain: 0, release: 0.025 }
    }).connect(this.master);
    this.pickupSynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: "triangle" },
      envelope: { attack: 0.004, decay: 0.16, sustain: 0.08, release: 0.24 },
      volume: AUDIO_FEEL_CONFIG.pickupVolumeDb
    }).connect(this.master);
    this.eventSynth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: "sine" },
      envelope: { attack: 0.01, decay: 0.26, sustain: 0.18, release: 0.7 },
      volume: AUDIO_FEEL_CONFIG.winVolumeDb
    }).connect(this.master);
  }

  private updateHeartbeat(dt: number, threat: number) {
    this.heartbeatCooldown -= dt;
    if (threat < AUDIO_FEEL_CONFIG.heartbeatThreshold || this.heartbeatCooldown > 0) return;
    const amount = (threat - AUDIO_FEEL_CONFIG.heartbeatThreshold) / (1 - AUDIO_FEEL_CONFIG.heartbeatThreshold);
    const interval = this.lerp(AUDIO_FEEL_CONFIG.heartbeatMaxInterval, AUDIO_FEEL_CONFIG.heartbeatMinInterval, Math.min(1, amount));
    this.heartbeatCooldown = interval;
    const time = this.reserveTime();
    this.safeTrigger("heartbeat", () => this.heartbeat?.triggerAttackRelease(54 + amount * 24, "16n", time, 0.34 + amount * 0.4));
  }

  private updateFootsteps(dt: number, speed: number, speedRatio: number, isChampion: boolean) {
    this.footstepCooldown -= dt;
    if (speed < AUDIO_FEEL_CONFIG.footstepMinSpeed || this.footstepCooldown > 0) return;
    this.footstepCooldown = AUDIO_FEEL_CONFIG.footstepInterval / Math.max(0.72, Math.min(1.35, speedRatio + 0.18));
    this.stepSide = 1 - this.stepSide;
    const velocity = isChampion ? 0.42 : 0.24 + speedRatio * 0.22;
    const time = this.reserveTime();
    this.safeTrigger("footstep", () => this.footstep?.triggerAttackRelease("32n", time, velocity));
  }

  /**
   * Returns a Tone start time that is strictly greater than every previously
   * reserved time, even when Tone.now() repeats (mobile audio-clock granularity).
   * `advance` reserves extra room for multi-note schedules (base + offset notes)
   * so the next trigger starts after them.
   */
  private reserveTime(advance = 0): number {
    const base = Math.max(Tone.now(), this.lastTriggerTime + TRIGGER_TIME_EPSILON);
    this.lastTriggerTime = base + Math.max(0, advance);
    return base;
  }

  /**
   * Safety net: an audio scheduling failure must degrade the sound, never the
   * gameplay loop. Logs and swallows so a Tone.js throw can't reach updatePlaying.
   */
  private safeTrigger(label: string, action: () => void) {
    try {
      action();
    } catch (error) {
      console.warn(`[EmotionalAudioSystem] disparo "${label}" omitido (audio):`, error);
    }
  }

  private lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
  }
}
